import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { clearBadge } from './push';
import VerificaOverlay from './VerificaOverlay';
import VerificaFab from './VerificaFab';

const Ctx = createContext(null);
export const useVerifica = () => useContext(Ctx);
const LS = 'verifica_multi_pending';

export function VerificaProvider({ children }) {
  const [job, setJob] = useState(null);          // { id, numero, stato, fase, e456, gea }
  const [overlayOpen, setOverlayOpen] = useState(false);
  const poll = useRef(null);
  const jobRef = useRef(null);
  const startingRef = useRef(false);
  useEffect(() => { jobRef.current = job; }, [job]);

  const stopPoll = useCallback(() => { if (poll.current) { clearInterval(poll.current); poll.current = null; } }, []);

  const track = useCallback((id) => {
    stopPoll();
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/verifica-multi/${id}`);
        setJob(r.data);
        if (r.data.stato === 'completato' || r.data.stato === 'errore') stopPoll();
      } catch (e) {
        // pending stantio (es. job perso dopo un riavvio del backend): pulisci e smetti
        if (e.response?.status === 404) {
          stopPoll();
          try { localStorage.removeItem(LS); } catch {}
          setJob(null); setOverlayOpen(false);
        }
        /* altri errori: ritenta al giro dopo */
      }
    };
    tick();
    poll.current = setInterval(tick, 2000);
  }, [stopPoll]);

  const start = useCallback(async (numero) => {
    const n = (numero || '').trim();
    if (!n) { toast.info('Inserisci un numero'); return; }
    if (startingRef.current || (jobRef.current && jobRef.current.stato === 'in_corso')) {
      toast.info('Una verifica è già in corso'); setOverlayOpen(true); return;
    }
    startingRef.current = true;   // guardia sincrona: chiude la finestra di race prima dell'await
    try {
      const r = await axios.post(`${API}/verifica-multi`, { numero: n });
      const id = r.data.id;
      try { localStorage.setItem(LS, JSON.stringify({ id, numero: r.data.numero })); } catch {}
      setJob({ id, numero: r.data.numero, stato: 'in_corso', fase: 'avvio', e456: null, gea: null });
      setOverlayOpen(true);
      track(id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore avvio verifica');
    } finally {
      startingRef.current = false;
    }
  }, [track]);

  const openOverlay = useCallback(() => { setOverlayOpen(true); clearBadge(); }, []);
  const closeOverlay = useCallback(() => setOverlayOpen(false), []);
  const clear = useCallback(() => {
    stopPoll();
    try { localStorage.removeItem(LS); } catch {}
    setJob(null); setOverlayOpen(false);
  }, [stopPoll]);

  // riaggancio all'avvio dell'app
  useEffect(() => {
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(LS) || 'null'); } catch {}
    if (saved && saved.id) {
      setJob({ id: saved.id, numero: saved.numero, stato: 'in_corso', fase: 'avvio', e456: null, gea: null });
      track(saved.id);
    }
    return () => stopPoll();
  }, [track, stopPoll]);

  const ready = !!job && (job.stato === 'completato' || job.stato === 'errore');
  const active = !!job;

  return (
    <Ctx.Provider value={{ job, active, ready, overlayOpen, start, openOverlay, closeOverlay, clear }}>
      {children}
      {active && !overlayOpen && <VerificaFab ready={ready} errored={job?.stato === 'errore'} onClick={openOverlay} />}
      {overlayOpen && <VerificaOverlay />}
    </Ctx.Provider>
  );
}
